"use client"

import { useEffect, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Button } from "../../ui/button"

const apiUrl = process.env.REACT_APP_API_URL || window.location.origin

const CollaborationModule = ({ actionId }) => {
  const { getAccessTokenSilently } = useAuth0()
  const [collaborations, setCollaborations] = useState([])
  const [targetGroupId, setTargetGroupId] = useState("")
  const [inviteMessage, setInviteMessage] = useState("")
  const [deadline, setDeadline] = useState("")
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState("")

  const authHeaders = async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
    })
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }
  }

  const loadCollaborations = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/collaborations`, {
        headers: await authHeaders(),
      })
      if (!response.ok) {
        throw new Error("Unable to load collaboration invites.")
      }
      setCollaborations(await response.json())
    } catch (error) {
      setMessage(error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCollaborations()
  }, [actionId])

  const inviteGroup = async (event) => {
    event.preventDefault()
    setMessage("")
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/collaborations`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({
        targetGroupId: Number(targetGroupId),
        inviteMessage,
        acceptanceDeadline: deadline || null,
      }),
    })
    if (!response.ok) {
      setMessage("That invite could not be sent. Check the group ID and deadline.")
      return
    }
    setTargetGroupId("")
    setInviteMessage("")
    setDeadline("")
    await loadCollaborations()
  }

  const respond = async (collaborationId, responseValue) => {
    setMessage("")
    const response = await fetch(`${apiUrl}/api/Actions/${actionId}/collaborations/${collaborationId}/respond`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ response: responseValue }),
    })
    if (!response.ok) {
      setMessage("Unable to respond to that invite.")
      return
    }
    await loadCollaborations()
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-yellow-500">Shared action</p>
        <h2 className="text-2xl font-bold">Invite another group to help</h2>
        <p className="mt-2 text-sm text-gray-400">
          When a group accepts, its members can help refine the plan and receive alerts about the shared work.
        </p>
      </div>

      <form className="grid gap-3 rounded-lg border border-yellow-500/20 bg-slate-950/60 p-4" onSubmit={inviteGroup}>
        <label className="text-sm font-semibold">
          Group ID to invite
          <input
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
            min="1"
            required
            type="number"
            value={targetGroupId}
            onChange={(event) => setTargetGroupId(event.target.value)}
          />
        </label>
        <label className="text-sm font-semibold">
          Note for that group
          <textarea
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
            rows="3"
            value={inviteMessage}
            onChange={(event) => setInviteMessage(event.target.value)}
            placeholder="Tell them why their group could help."
          />
        </label>
        <label className="text-sm font-semibold">
          Reply by
          <input
            className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-white"
            type="datetime-local"
            value={deadline}
            onChange={(event) => setDeadline(event.target.value)}
          />
        </label>
        <Button type="submit">Send collaboration invite</Button>
      </form>

      {message && <div className="rounded border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm">{message}</div>}

      <div className="space-y-3">
        <h3 className="text-lg font-bold">Collaboration invites</h3>
        {loading && <p className="text-sm text-gray-400">Loading invites...</p>}
        {!loading && collaborations.length === 0 && (
          <p className="rounded border border-slate-700 p-4 text-sm text-gray-400">
            No collaboration invites yet. Invite another group when the work would be stronger together.
          </p>
        )}
        {collaborations.map((item) => (
          <div key={item.id} className="rounded-lg border border-slate-700 bg-slate-950/70 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-bold">{item.targetGroupName || `Group ${item.targetGroupId}`}</p>
                <p className="text-sm text-gray-400">
                  {item.status} · reply by {new Date(item.acceptanceDeadline).toLocaleString()}
                </p>
                {item.inviteMessage && <p className="mt-2 text-sm">{item.inviteMessage}</p>}
              </div>
              {item.status === "Pending" && (
                <div className="flex gap-2">
                  <Button type="button" onClick={() => respond(item.id, "Accepted")}>
                    Accept
                  </Button>
                  <Button type="button" variant="outline" onClick={() => respond(item.id, "Declined")}>
                    Decline
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CollaborationModule
